import styled from '@emotion/styled'

import { LinkButton, Space } from '../../atoms'
import { AddRemoveButton } from '../../atoms/buttons/add-remove-button'
import Icon from '../../atoms/Icon'
import { NumberInput } from '../../atoms/number-input'
import { Percent } from '../../atoms/percent'
import { StyledAssetTable } from './primitives'

const Th = styled.th`
  white-space: break-spaces;
`

export type Asset = {
  id: string
  investmentWeight?: number
  name: string
  return: number
  volatility: number
  weight?: number
}

export type EditableAssetTableProps = {
  assets?: Asset[]
  isFundOfFund?: boolean
  isNew?: boolean
  onChangeAssetWeight: (id: string, weight: number) => void
  onClickAsset?: (id: string) => void
  onRemoveAsset: (id: string) => void
  readonly?: boolean
}

function round(val: number, pow: number) {
  const exp = 10 ** pow
  const res = Math.round(val * exp) / exp
  return res
}

const RemoveButton = ({
  asset,
  onRemoveAsset
}: {
  asset: Asset
  onRemoveAsset: (id: string) => void
}) => (
  <AddRemoveButton
    isDisabled={false}
    operationType="remove"
    onClick={() => onRemoveAsset(asset.id)}
  >
    <Space isflex alignItems="center" justifyContent="center">
      <Icon name="remove" size="small" />
    </Space>
  </AddRemoveButton>
)

export function EditableAssetTable({
  assets,
  isFundOfFund,
  isNew,
  onChangeAssetWeight,
  onClickAsset,
  onRemoveAsset,
  readonly
}: EditableAssetTableProps) {
  return (
    <StyledAssetTable>
      <thead>
        <tr>
          <th className="asset">Asset</th>
          <th className="return">Return</th>
          <th className="volatility">Volatility</th>
          <Th className="weight">{`${isFundOfFund ? 'Target\n' : ''}weight (%)`}</Th>
          {isFundOfFund && <Th className="investment">{`Investment \nweight (%)`}</Th>}
        </tr>
      </thead>
      <tbody>
        {Array.isArray(assets) &&
          assets.map(asset => (
            <tr key={asset.name}>
              <td className="asset">
                {onClickAsset !== undefined ? (
                  <LinkButton underline onClick={() => onClickAsset(asset.id)}>
                    {asset.name}
                  </LinkButton>
                ) : (
                  asset.name
                )}
              </td>
              <td className="return">
                <Percent
                  fixed
                  highlightPnl
                  numDecimalPlaces={2}
                  value={asset.return === null ? null : asset.return * 100}
                />
              </td>
              <td className="volatility">
                <Percent
                  fixed
                  highlightPnl={false}
                  numDecimalPlaces={2}
                  value={asset.volatility === null ? null : asset.volatility * 100}
                />
              </td>
              <td className="weight">
                <Space isflex alignItems="center" justifyContent="center">
                  <NumberInput
                    readOnly={readonly}
                    value={
                      asset.weight === null || asset.weight === 0
                        ? ''
                        : round(asset.weight * 100, 2)
                    }
                    onChange={v =>
                      onChangeAssetWeight(asset.id, round(Number(v.target.value) / 100, 4))
                    }
                  />
                  {!readonly && !isFundOfFund && (
                    <RemoveButton asset={asset} onRemoveAsset={onRemoveAsset} />
                  )}
                </Space>
              </td>
              {isFundOfFund && (
                <td className="weight">
                  <Space isflex alignItems="center" justifyContent="center">
                    {isNew ? (
                      <Percent
                        fixed
                        highlightPnl={false}
                        numDecimalPlaces={2}
                        value={asset.weight ? round(asset.weight * 100, 2) : 0}
                      />
                    ) : (
                      round(asset.investmentWeight * 100, 2) || 'N/A'
                    )}
                    <Space ml={3}>
                      {!readonly && isFundOfFund && (
                        <RemoveButton asset={asset} onRemoveAsset={onRemoveAsset} />
                      )}
                    </Space>
                  </Space>
                </td>
              )}
            </tr>
          ))}
      </tbody>
    </StyledAssetTable>
  )
}
